/**
 * § Focus, where a popover puts the focus as it opens (2026-10-02, by the owner's
 * report: "please dont show this tooltip when we first open it... a lot of pages do that"): on its first
 * meaningful control, or on the panel itself when it holds none, following the WAI-ARIA dialog pattern.
 *
 * Radix's own choice is the first tabbable element that is not a link. That put the focus of a meeting's
 * preview on its series menu's "⋯" button, after Open meeting, because Open meeting is a link drawn as
 * the primary button. Two things differ here. A link counts, because in Baton's panels a link is an
 * action the panel offers (Open meeting, a member's page), not fine print. An icon-only button
 * (`IconButton`, `data-icon-only`) is passed over, because it is always a secondary control (a close, a
 * "⋯" menu, an info mark) whose name is only in its tooltip, so focus landing on it says nothing and
 * shows a tip nobody asked for. So is a control disabled with a reason (`aria-disabled`, 2026-10-10),
 * which stays focusable so its reason can be reached, and whose tip would otherwise open with the
 * panel. Everything else matches Radix: an element is a candidate when its
 * `tabIndex` is 0 or more and it is not disabled, hidden or a hidden input, nor inside a part of the
 * panel drawn with `display: none` or `visibility: hidden`; the focus does not scroll the page; and a
 * text field it lands on has its text selected, so typing replaces the value.
 *
 * `Popover` passes it as the Radix content's `onOpenAutoFocus`, which hands it the event dispatched on
 * the panel. A dialog and a drawer put the focus on their panel instead (`Dialog`, `Drawer`).
 */
export function focusFirstMeaningfulControl(event: Event): void {
  event.preventDefault();
  const panel = event.target;
  if (!(panel instanceof HTMLElement)) return;
  const target = firstMeaningfulControl(panel) ?? panel;
  target.focus({ preventScroll: true });
  if (target instanceof HTMLInputElement) target.select();
}

function firstMeaningfulControl(panel: HTMLElement): HTMLElement | null {
  const walker = document.createTreeWalker(panel, NodeFilter.SHOW_ELEMENT, {
    acceptNode: (node) => (node instanceof HTMLElement && candidate(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP),
  });
  for (let node = walker.nextNode(); node !== null; node = walker.nextNode()) {
    if (node instanceof HTMLElement && !hiddenWithin(node, panel)) return node;
  }
  return null;
}

function candidate(element: HTMLElement): boolean {
  if (element.tabIndex < 0 || element.hidden || element.getAttribute("aria-disabled") === "true") return false;
  if ((element instanceof HTMLButtonElement || element instanceof HTMLInputElement || element instanceof HTMLSelectElement || element instanceof HTMLTextAreaElement) && element.disabled) return false;
  if (element instanceof HTMLInputElement && element.type === "hidden") return false;
  return element.dataset.iconOnly !== "true";
}

function hiddenWithin(element: HTMLElement, panel: HTMLElement): boolean {
  if (getComputedStyle(element).visibility === "hidden") return true;
  for (let node: HTMLElement | null = element; node !== null && node !== panel; node = node.parentElement) {
    if (getComputedStyle(node).display === "none") return true;
  }
  return false;
}
